import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import type { RowSelectionState } from '@tanstack/react-table';
import { Check, Filter } from 'lucide-react';
import { FaFileExcel } from 'react-icons/fa';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import i18n from '@/utils/i18n';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { downloadJsonDataAsExcelFile } from '@/utils/download';
import { LayoutStoreInstance } from '@/LayoutStore';
import { DateFormatOptionsForFileNames } from '@/utils/i18n';
import { centsToPrice } from '@/utils/formatter';
import { DataTable } from '@/views/Events/EventOwnerDashboard/HandoutsReturnsTable/DataTable';
import {
  columns,
  getMwkEventOverviewRowId,
  MwkEventOverviewRow,
  resolvedMwkEventOverviewCurrency,
} from './columns';
import { EventOverviewDateRange } from './EventOverviewDateRange';
import {
  buildEventOverviewExcelTotalRow,
  EventOverviewTableFooter,
} from './EventOverviewTableFooter';

function createInitialDateRange(): DateRange {
  const to = new Date();
  const from = new Date();
  from.setDate(1);
  from.setHours(0, 0, 0, 0);
  to.setHours(0, 0, 0, 0);
  return { from, to };
}

const EventOverviewPage = () => {
  useDocumentTitle(i18n.t('EventOverview:title'));

  const initialRange = useMemo(() => createInitialDateRange(), []);
  const [draftRange, setDraftRange] = useState<DateRange | undefined>(
    initialRange,
  );
  const [appliedRange, setAppliedRange] = useState<DateRange | undefined>(
    initialRange,
  );
  const [data, setData] = useState<MwkEventOverviewRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [selectedCurrencies, setSelectedCurrencies] = useState<Set<string>>(
    new Set(),
  );

  const fetchData = useCallback(async () => {
    if (!appliedRange?.from) {
      return;
    }
    const from = appliedRange.from;
    const to = appliedRange.to ?? from;
    const startDate = format(from, 'yyyy-MM-dd');
    const endDate = format(to, 'yyyy-MM-dd');

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        startDate: startDate + 'T00:00:00',
        endDate: endDate + 'T23:59:59',
      });
      const res = await fetch(
        `/api/3/MwkBilling/GetEventOverview?${params.toString()}`,
        { credentials: 'include' },
      );
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || `HTTP ${res.status}`);
      }
      const json = await res.json();
      setData(json);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to load data';
      setError(message);
      LayoutStoreInstance.setError(`${i18n.t('Common:error')}: ${message}`);
    } finally {
      setLoading(false);
    }
  }, [appliedRange]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  useEffect(() => {
    setRowSelection({});
  }, [data]);

  const handleApplyDateRange = useCallback(() => {
    setAppliedRange(draftRange);
  }, [draftRange]);

  const availableCurrencies = useMemo(
    () =>
      [
        ...new Set([
          'EUR',
          'GBP',
          ...data.map((r) => resolvedMwkEventOverviewCurrency(r)),
        ]),
      ].sort(),
    [data],
  );

  const filteredData = useMemo(() => {
    if (selectedCurrencies.size === 0) {
      return data;
    }
    return data.filter((r) =>
      selectedCurrencies.has(resolvedMwkEventOverviewCurrency(r)),
    );
  }, [data, selectedCurrencies]);

  const toggleCurrency = useCallback((currency: string) => {
    setSelectedCurrencies((prev) => {
      const next = new Set(prev);
      if (next.has(currency)) {
        next.delete(currency);
      } else {
        next.add(currency);
      }
      return next;
    });
  }, []);

  const handleDownloadExcel = useCallback(() => {
    const selectedIds = Object.keys(rowSelection).filter(
      (id) => rowSelection[id],
    );
    const rowsSource =
      selectedIds.length > 0
        ? filteredData.filter((r) =>
            selectedIds.includes(getMwkEventOverviewRowId(r)),
          )
        : filteredData;

    const rows: Array<Record<string, string | number>> = rowsSource.map(
      (r) => ({
        [i18n.t('EventOverview:event')]: r.eventName,
        [i18n.t('EventOverview:containerTypeId')]: r.containerTypeId,
        [i18n.t('EventOverview:containerTypeName')]: r.containerTypeName,
        [i18n.t('EventOverview:packedAfterCleaning')]: r.packedAfterCleaning,
        [i18n.t('EventOverview:amountsFullBoxes')]: r.amountsFullBoxes,
        [i18n.t('EventOverview:leftOverAmounts')]: r.leftOverAmounts ?? 0,
        [i18n.t('EventOverview:containerAmounts')]: r.containerAmounts,
        [i18n.t('EventOverview:picking')]: r.picking ?? 0,
        [i18n.t('EventOverview:returnedSealed')]: r.returnedSealed ?? 0,
        [i18n.t('EventOverview:countedBoxes')]: r.countedBoxes ?? 0,
        [i18n.t('EventOverview:logisticPartnerCleaningPrice')]: centsToPrice(
          r.logisticPartnerCleaningPrice ?? 0,
          resolvedMwkEventOverviewCurrency(r),
        ),
      }),
    );
    const totalRow = buildEventOverviewExcelTotalRow(rowsSource);
    if (totalRow) {
      rows.push(totalRow);
    }
    const dateStr = new Date()
      .toLocaleDateString(i18n.language, DateFormatOptionsForFileNames)
      .replaceAll('/', '-');
    downloadJsonDataAsExcelFile(rows, `mwk-event-overview-${dateStr}`);
  }, [filteredData, rowSelection]);

  const toolbarRight =
    data.length > 0 ? (
      <Button type="button" size="sm" onClick={handleDownloadExcel}>
        <FaFileExcel className="tw:mr-2" />
        {i18n.t('EventOverview:downloadExcel')}
      </Button>
    ) : null;

  return (
    <div className="tw:container tw:flex tw:flex-col tw:gap-8">
      <h1 className="tw:text-[40px] tw:font-semibold tw:leading-none tw:tracking-[-0.4px] tw:text-black tw:dark:text-foreground">
        {i18n.t('EventOverview:title')}
      </h1>

      <div className="tw:flex tw:flex-col tw:gap-4">
        <p className="tw:text-muted-foreground tw:text-base">
          {i18n.t('EventOverview:subtitle')}
        </p>

        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
          <EventOverviewDateRange
            value={draftRange}
            onChange={setDraftRange}
            onApply={handleApplyDateRange}
            loading={loading}
          />

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="tw:border-dashed tw:gap-2">
                <Filter className="tw:size-4" />
                {i18n.t('EventOverview:currency')}
                {selectedCurrencies.size > 0 && (
                  <>
                    <Separator
                      orientation="vertical"
                      className="tw:mx-1 tw:h-full"
                    />
                    <div className="tw:flex tw:gap-1">
                      {availableCurrencies
                        .filter((c) => selectedCurrencies.has(c))
                        .map((c) => (
                          <Badge
                            variant="secondary"
                            key={c}
                            className="tw:rounded-sm tw:px-1 tw:font-normal"
                          >
                            {c}
                          </Badge>
                        ))}
                    </div>
                  </>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="tw:w-fit tw:min-w-[120px] tw:p-0"
              align="start"
            >
              <Command>
                <CommandList>
                  <CommandEmpty>
                    {i18n.t('ItemLocation:noResults')}
                  </CommandEmpty>
                  <CommandGroup>
                    {availableCurrencies.map((currency) => {
                      const isSelected = selectedCurrencies.has(currency);
                      return (
                        <CommandItem
                          key={currency}
                          onSelect={() => toggleCurrency(currency)}
                        >
                          <div
                            className={cn(
                              'tw:flex tw:size-4 tw:items-center tw:justify-center tw:rounded-[4px] tw:border',
                              isSelected
                                ? 'tw:bg-primary tw:border-primary tw:text-primary-foreground'
                                : 'tw:border-input tw:[&_svg]:invisible',
                            )}
                          >
                            <Check className="tw:text-primary-foreground tw:size-3.5" />
                          </div>
                          <span>{currency}</span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                  {selectedCurrencies.size > 0 && (
                    <>
                      <CommandSeparator />
                      <CommandGroup>
                        <CommandItem
                          onSelect={() => setSelectedCurrencies(new Set())}
                          className="tw:justify-center tw:text-center"
                        >
                          {i18n.t('Common:clearFilters')}
                        </CommandItem>
                      </CommandGroup>
                    </>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        <DataTable
          columns={columns}
          data={filteredData}
          isLoading={loading}
          error={error}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          opts={{
            ToolbarRightComponent: toolbarRight,
            enableRowSelection: true,
            getRowId: getMwkEventOverviewRowId,
            showSelectedRowCountInPagination: false,
            tableFooter:
              !loading && filteredData.length > 0
                ? ({ table }) => <EventOverviewTableFooter table={table} />
                : undefined,
          }}
        />
      </div>
    </div>
  );
};

export default EventOverviewPage;
